import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue
} from '@/components/ui/multi-select';

// GraphQL queries for searching the three supported models
const SEARCH_CONTACTS = gql`
  query SearchContacts($name: String, $first: Int, $after: String) {
    contacts(name: $name, first: $first, after: $after) {
      edges {
        node {
          id
          name
          email
          function
          companyName
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const SEARCH_ACCOUNTS = gql`
  query SearchAccounts($name: String, $first: Int, $after: String) {
    accounts(name: $name, first: $first, after: $after) {
      edges {
        node {
          id
          name
          email
          city
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const SEARCH_EMPLOYEES = gql`
  query SearchEmployees($name: String, $first: Int, $after: String) {
    hrEmployees(name: $name, first: $first, after: $after) {
      edges {
        node {
          id
          name
          workEmail
          email
          departmentId {
            name
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

interface Account {
  city?: null | string;
  email?: null | string;
  id: string;
  name?: null | string;
}

interface Contact {
  companyName?: null | string;
  email?: null | string;
  function?: null | string;
  id: string;
  name?: null | string;
}

interface Employee {
  departmentId?: {
    name: string;
  } | null;
  email?: null | string;
  id: string;
  name?: null | string;
  workEmail?: null | string;
}

interface ForeignKeyMultiSelectProps {
  field: {
    name: string;
    relation?: string;
    ttype: string;
  };
  onChange: (values: string[]) => void;
  placeholder?: string;
  values: string[];
}

export function ForeignKeyMultiSelect({
  field,
  onChange,
  placeholder = 'Search and select...',
  values
}: ForeignKeyMultiSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [allResults, setAllResults] = useState<
    Map<string, Account | Contact | Employee>
  >(new Map());
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<null | string>(null);

  const modelType = getModelType(field.relation);

  // Determine which query to use - use data directly instead of onCompleted
  const [
    searchContacts,
    { data: contactsData, error: contactsError, loading: loadingContacts }
  ] = useLazyQuery<{
    contacts: {
      edges: { cursor: string; node: Contact }[];
      pageInfo: { endCursor?: null | string; hasNextPage: boolean };
    };
  }>(SEARCH_CONTACTS, {
    fetchPolicy: 'network-only'
  });

  const [
    searchAccounts,
    { data: accountsData, error: accountsError, loading: loadingAccounts }
  ] = useLazyQuery<{
    accounts: {
      edges: { cursor: string; node: Account }[];
      pageInfo: { endCursor?: null | string; hasNextPage: boolean };
    };
  }>(SEARCH_ACCOUNTS, {
    fetchPolicy: 'network-only'
  });

  // Track the current search term to prevent stale callbacks
  const currentSearchTermRef = useRef<string>('');

  const [
    searchEmployees,
    { data: employeesData, error: employeesError, loading: loadingEmployees }
  ] = useLazyQuery<{
    hrEmployees: {
      edges: { cursor: string; node: Employee }[];
      pageInfo: { endCursor?: null | string; hasNextPage: boolean };
    };
  }>(SEARCH_EMPLOYEES, {
    fetchPolicy: 'network-only'
  });

  // Process data when it changes - this is more reliable than onCompleted
  useEffect(() => {
    if (contactsData?.contacts?.edges) {
      setAllResults(prev => {
        const newResults = new Map(prev);
        contactsData.contacts.edges.forEach(edge => {
          if (edge?.node?.id) {
            newResults.set(edge.node.id, edge.node);
          }
        });
        return newResults;
      });
      setHasMore(contactsData.contacts.pageInfo?.hasNextPage ?? false);
      setEndCursor(contactsData.contacts.pageInfo?.endCursor || null);
    }
  }, [contactsData]);

  useEffect(() => {
    if (accountsData?.accounts?.edges) {
      setAllResults(prev => {
        const newResults = new Map(prev);
        accountsData.accounts.edges.forEach(edge => {
          if (edge?.node?.id) {
            newResults.set(edge.node.id, edge.node);
          }
        });
        return newResults;
      });
      setHasMore(accountsData.accounts.pageInfo?.hasNextPage ?? false);
      setEndCursor(accountsData.accounts.pageInfo?.endCursor || null);
    }
  }, [accountsData]);

  useEffect(() => {
    if (employeesData?.hrEmployees?.edges) {
      setAllResults(prev => {
        const newResults = new Map(prev);
        employeesData.hrEmployees.edges.forEach(edge => {
          if (edge?.node?.id) {
            newResults.set(edge.node.id, edge.node);
          }
        });
        return newResults;
      });
      setHasMore(employeesData.hrEmployees.pageInfo?.hasNextPage ?? false);
      setEndCursor(employeesData.hrEmployees.pageInfo?.endCursor || null);
    }
  }, [employeesData]);

  // Log errors
  useEffect(() => {
    if (contactsError) {
      console.error(
        '[ForeignKeyMultiSelect] searchContacts error:',
        contactsError
      );
    }
    if (accountsError) {
      console.error(
        '[ForeignKeyMultiSelect] searchAccounts error:',
        accountsError
      );
    }
    if (employeesError) {
      console.error(
        '[ForeignKeyMultiSelect] searchEmployees error:',
        employeesError
      );
    }
  }, [contactsError, accountsError, employeesError]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Track previous search term to detect changes
  const prevDebouncedTerm = useRef<null | string>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip reset on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevDebouncedTerm.current = debouncedSearchTerm;
      return;
    }

    // Only reset if we had a previous search term and it's different
    // But don't reset if we're going from a search term to empty (let user see previous results)
    if (
      prevDebouncedTerm.current !== null &&
      prevDebouncedTerm.current !== debouncedSearchTerm &&
      debouncedSearchTerm !== ''
    ) {
      // Search term changed to a new non-empty value, reset results for new search
      setAllResults(new Map());
      setEndCursor(null);
    }
    prevDebouncedTerm.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  // Perform search when debounced term changes or component mounts
  useEffect(() => {
    if (!modelType) return;

    const variables: any = {
      first: debouncedSearchTerm ? 20 : 50 // Load more initially
    };

    if (debouncedSearchTerm) {
      variables.name = debouncedSearchTerm;
    }

    // Update the current search term ref before triggering search
    currentSearchTermRef.current = debouncedSearchTerm || '';

    if (modelType === 'contact') {
      searchContacts({ variables });
    } else if (modelType === 'account') {
      searchAccounts({ variables });
    } else if (modelType === 'employee') {
      searchEmployees({ variables });
    }
  }, [
    debouncedSearchTerm,
    modelType,
    searchContacts,
    searchAccounts,
    searchEmployees
  ]);

  const loading = loadingContacts || loadingAccounts || loadingEmployees;

  // Format display label for a record
  const getDisplayLabel = (record: Account | Contact | Employee): string => {
    if ('name' in record && record.name) {
      let label = record.name;

      // Add additional context
      if ('email' in record && record.email) {
        label += ` (${record.email})`;
      } else if ('workEmail' in record && record.workEmail) {
        label += ` (${record.workEmail})`;
      }

      // Add department for employees
      if ('departmentId' in record && record.departmentId?.name) {
        label += ` - ${record.departmentId.name}`;
      }

      // Add function/company for contacts
      if ('function' in record && record.function) {
        label += ` - ${record.function}`;
      }
      if ('companyName' in record && record.companyName) {
        label += ` @ ${record.companyName}`;
      }

      return label;
    }
    return record.id;
  };

  // Get all results as array, sorted by selection status
  const resultsArray = useMemo(() => {
    const results = Array.from(allResults.values());
    // Sort: selected items first, then by name
    const sorted = results.sort((a, b) => {
      const aSelected = values.includes(a.id);
      const bSelected = values.includes(b.id);
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }
      const aName = ('name' in a ? a.name : '') || '';
      const bName = ('name' in b ? b.name : '') || '';
      return aName.localeCompare(bName);
    });
    return sorted;
  }, [allResults, values]);

  if (!modelType) {
    // Fallback to regular input if model type is not supported
    return null;
  }

  return (
    <MultiSelect
      onValuesChange={onChange}
      values={values}
    >
      <MultiSelectTrigger className="w-full">
        <MultiSelectValue placeholder={placeholder} />
      </MultiSelectTrigger>
      <MultiSelectContent
        onSearchChange={setSearchTerm}
        search={{ placeholder: `Search ${modelType}s...` }}
      >
        <MultiSelectGroup>
          {resultsArray.map(record => {
            const label = getDisplayLabel(record);
            return (
              <MultiSelectItem
                key={record.id}
                value={record.id}
              >
                {label}
              </MultiSelectItem>
            );
          })}
          {loading && resultsArray.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-gray-500">Loading...</div>
          )}
          {!loading && resultsArray.length === 0 && debouncedSearchTerm && (
            <div className="px-2 py-1.5 text-sm text-gray-500">
              No {modelType}s found matching "{debouncedSearchTerm}"
            </div>
          )}
          {!loading && resultsArray.length === 0 && !debouncedSearchTerm && (
            <div className="px-2 py-1.5 text-sm text-gray-500">
              Start typing to search {modelType}s...
            </div>
          )}
          {hasMore && resultsArray.length > 0 && (
            <div className="px-2 py-1.5 text-xs text-gray-400">
              Scroll for more results...
            </div>
          )}
        </MultiSelectGroup>
      </MultiSelectContent>
    </MultiSelect>
  );
}

// Map Odoo model names to GraphQL query types
function getModelType(
  relation?: string
): 'account' | 'contact' | 'employee' | null {
  if (!relation) return null;

  const relationLower = relation.toLowerCase();
  if (
    relationLower.includes('res.partner') ||
    relationLower.includes('contact')
  ) {
    return 'contact';
  }
  if (
    relationLower.includes('opstream.account') ||
    relationLower.includes('account')
  ) {
    return 'account';
  }
  if (
    relationLower.includes('hr.employee') ||
    relationLower.includes('employee')
  ) {
    return 'employee';
  }

  return null;
}
